/**
 * Каркас приложения: верхняя панель с навигацией, маршруты экранов, уведомления.
 *
 * Игрок:          /  — лобби или стол;  /profile — профиль;  /profile/games/:id — повтор розыгрыша.
 * Администратор:  /admin — обзор, пользователи, розыгрыши;  /admin/users/:id;  /admin/games/:id.
 * Без входа — экран входа и регистрации; /auth/complete — возврат от провайдера SSO.
 */
import { useEffect, useState } from 'react';
import { cr } from '../lib/format';
import { Link, match, navigate, usePath } from '../lib/router';
import { updateSettings, useSettings } from '../lib/settings';
import { client, useApp } from '../net/client';
import { AdminPage } from './admin/AdminPage';
import { LoginPage, useAuthComplete } from './auth/LoginPage';
import { Avatar } from './kit';
import { Lobby } from './Lobby';
import { ProfilePage } from './profile/ProfilePage';
import { ReplayPage } from './profile/ReplayPage';
import { Rules } from './Rules';
import { Table } from './Table';

export function App() {
  useAuthComplete();
  const conn = useApp((s) => s.conn);
  const toasts = useApp((s) => s.toasts);
  return (
    <div className="app">
      <TopBar />
      {conn !== 'open' && <ConnBanner />}
      <main>
        <Screen />
      </main>
      <div className="toasts" aria-live="assertive">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.text}
          </div>
        ))}
      </div>
    </div>
  );
}

function Screen() {
  const path = usePath();
  const needLogin = useApp((s) => s.needLogin);
  const role = useApp((s) => s.role);
  const profile = useApp((s) => s.profile);
  const room = useApp((s) => s.room);

  // адреса другой роли недоступны: администратор не играет, игрок не видит администрирование
  useEffect(() => {
    if (role === 'admin' && !path.startsWith('/admin')) navigate('/admin', { replace: true });
    if (role === 'player' && path.startsWith('/admin')) navigate('/', { replace: true });
  }, [role, path]);

  if (path === '/auth/complete') return <div className="center dim">Выполняется вход…</div>;
  if (needLogin) return <LoginPage />;
  if (!role) return <div className="center dim">Загрузка…</div>;

  if (role === 'admin') {
    const u = match('/admin/users/:id', path);
    if (u) return <ProfilePage userId={u.id!} />;
    const g = match('/admin/games/:id', path);
    if (g) return <ReplayPage gameId={g.id!} admin />;
    return <AdminPage />;
  }

  if (!profile) return <div className="center dim">Загрузка…</div>;
  const g = match('/profile/games/:id', path);
  const page = path === '/profile' ? <ProfilePage /> : g ? <ReplayPage gameId={g.id!} admin={false} /> : null;
  if (page)
    return (
      <>
        {room && room.meSeated && <BackToTable />}
        {page}
      </>
    );
  return room ? <Table room={room} /> : <Lobby />;
}

function BackToTable() {
  const game = useApp((s) => s.game);
  const active = game && !game.settlement && game.me.participant;
  return (
    <div className={`back-banner ${active ? 'hot' : ''}`}>
      <span>{active ? 'Идёт ваш розыгрыш' : 'Вы сидите за столом'}</span>
      <Link className="btn tiny primary" to="/">
        Вернуться за стол
      </Link>
    </div>
  );
}

function TopBar() {
  const profile = useApp((s) => s.profile);
  const role = useApp((s) => s.role);
  const adminLogin = useApp((s) => s.adminLogin);
  const info = useApp((s) => s.info);
  const path = usePath();
  const settings = useSettings();
  const [rules, setRules] = useState(false);
  const [menu, setMenu] = useState(false);
  useEffect(() => setMenu(false), [path]);
  const nav =
    role === 'admin'
      ? [{ to: '/admin', title: 'Администрирование', on: path.startsWith('/admin') }]
      : role === 'player'
        ? [
            { to: '/', title: 'Лобби', on: path === '/' },
            { to: '/profile', title: 'Профиль', on: path.startsWith('/profile') },
          ]
        : [];
  return (
    <header className="topbar">
      <Link to={role === 'admin' ? '/admin' : '/'} className="brand">
        Рыночный стол
      </Link>
      <span className="demo-badge" title="Игровые кредиты не имеют денежной стоимости">
        ДЕМО<span className="hide-sm"> · игровые кредиты · без реальных денег</span>
      </span>
      <nav className="top-nav" aria-label="Разделы">
        {nav.map((n) => (
          <Link key={n.to} to={n.to} className={n.on ? 'on' : ''} aria-current={n.on ? 'page' : undefined}>
            {n.title}
          </Link>
        ))}
      </nav>
      <span className="spacer" />
      {info && (
        <button className="btn ghost tiny hide-sm" onClick={() => setRules(true)}>
          Правила
        </button>
      )}
      <button className="btn ghost tiny icon" onClick={() => updateSettings((s) => ({ ...s, theme: s.theme === 'dark' ? 'light' : 'dark' }))} aria-label="Сменить тему" title="Сменить тему">
        {settings.theme === 'dark' ? '☀' : '☾'}
      </button>
      {profile && (
        <span className="balance-chip mono" title="Баланс игровых кредитов">
          {cr(profile.balance)}
        </span>
      )}
      {(profile || role === 'admin') && (
        <div className="account">
          <button className="account-btn" onClick={() => setMenu((v) => !v)} aria-expanded={menu} aria-haspopup="menu">
            {profile ? <Avatar id={profile.id} name={profile.name} size={28} /> : <span className="avatar admin">A</span>}
            <span className="hide-sm">{profile?.name ?? adminLogin}</span>
          </button>
          {menu && (
            <div className="account-menu" role="menu">
              {profile && (
                <Link to="/profile" role="menuitem">
                  Профиль и история
                </Link>
              )}
              {info && (
                <button role="menuitem" onClick={() => (setMenu(false), setRules(true))}>
                  Правила
                </button>
              )}
              <button role="menuitem" onClick={() => client.logout()}>
                Выйти
              </button>
            </div>
          )}
        </div>
      )}
      {rules && info && <Rules info={info} onClose={() => setRules(false)} />}
    </header>
  );
}

/** Состояние соединения. После нескольких неудач показывает транспорт, адрес и код закрытия. */
function ConnBanner() {
  const conn = useApp((s) => s.conn);
  const ci = useApp((s) => s.connInfo);
  const text = conn === 'connecting' && ci.attempts === 0 ? 'Подключение к серверу…' : 'Нет связи с сервером — переподключение…';
  return (
    <div className="conn-banner">
      {text}
      {ci.attempts >= 2 && (
        <span className="conn-detail">
          {ci.transport === 'ws' ? 'WebSocket' : 'резервный HTTP-канал'} <code>{ci.url}</code>
          {ci.lastCode !== null ? ` (код ${ci.lastCode})` : ''}, попыток: {ci.attempts}.
          {ci.transport === 'ws' ? ' Если WebSocket в вашей сети недоступен, клиент сам перейдёт на резервный канал.' : ' Сервер недоступен и по HTTP — проверьте подключение к сети.'}
        </span>
      )}
    </div>
  );
}
